import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { defaultsDeep, isEqual } from 'lodash';
import { Subscription } from 'rxjs';

import { matExpansionAnimations } from '../animations/expansion.animations';
import { addExtraAccessParameters, addFilters, addRangeConstraint } from '../models/access-url';
import { DEFAULT_DATASET, determineDatasetType, IDataset, parseOffset } from '../models/dataset';
import { DatasetType } from '../models/dataset-type';
import { DEFAULT_FILTERS, IFilters } from '../models/filters';
import { IRange, sanitizeRange } from '../models/range';
import { DEFAULT_UI_OPTIONS, IUiOptions } from '../models/ui-options';
import { HighchartsConfigService } from '../services/config.service';
import { DatasetService } from '../services/dataset.service';
import { EventsService } from '../services/events.service';
import { FiltersService } from '../services/filters.service';
import { MenuOptionsService } from '../services/menu-options.service';
import { PlotsService } from '../services/plots.service';
import { TimeRangeService } from '../services/time-range';
import { UiOptionsService } from '../services/ui-options.service';
import { createUID } from '../services/util';

/** Base container for a scicharts plot. */
@Component({
    selector: 'sci-charts',
    templateUrl: './scicharts.container.html',
    styleUrls: [ './scicharts.container.scss', './colors.scss' ],
    animations: [
        matExpansionAnimations.bodyExpansion
    ]
})
export class SciChartsComponent implements OnChanges, OnDestroy {
    @Input() datasets: IDataset[];
    @Input() range: IRange;
    @Input() plotId: string;
    @Output() closeChart: EventEmitter<string> = new EventEmitter<string>();
    emptyDatasets: IDataset[] = [];
    initialized = false;
    loading = true;
    hasEvents = false;
    eventsLoaded = false;
    error: string;
    disabled = true;
    subscriptions: Subscription[] = [];
    plotType: 'LINE' | 'IMAGE' | 'EVENT_TABLE';
    filters = DEFAULT_FILTERS;
    uiOptions: IUiOptions = DEFAULT_UI_OPTIONS;

    constructor(
        private _timeRangeService: TimeRangeService,
        private _datasetService: DatasetService,
        private _filtersService: FiltersService,
        private _configService: HighchartsConfigService,
        private _menuOptionsService: MenuOptionsService,
        private _plotsService: PlotsService,
        private _uiOptionsService: UiOptionsService,
        private _eventsService: EventsService
    ) {}

    ngOnChanges( changes: SimpleChanges ) {
        if ( changes.hasOwnProperty('range') && changes.range.currentValue != null ) {
            // range can be a number or date, convert either to a number value we can use consistently
            this.range = sanitizeRange( changes.range.currentValue );

            if ( changes.range.firstChange ) {
                this._timeRangeService.initPlotTimeRange( this.plotId, this.range );
            } else {
                this._timeRangeService.setPlotTimeRange( this.plotId, this.range );
                this.handleRangeChanges();
            }
        }

        if ( changes.hasOwnProperty('datasets') ) {
            this.datasets = changes.datasets.currentValue;
            if ( changes.datasets.isFirstChange ) {
                // on first change, register listeners for various configurations
                this._datasetService.initPlotDatasets( this.plotId, this.datasets );

                this.subscriptions.push( this._datasetService.getPlotDatasets$( this.plotId ).subscribe(
                    datasets => {
                        this.loading = datasets.loading;
                        if ( datasets.error != null ) {
                            this.error = ( datasets.error['message'] != null ) ? datasets.error['message'] : datasets.error;
                        }
                        if ( !this.loading ) {
                            this.emptyDatasets = this.datasets.filter( dataset => {
                                return dataset.data == null || dataset.data.length === 0;
                            });
                        }
                    }
                ));

                this.subscriptions.push( this._timeRangeService.getPlotTimeRange$( this.plotId ).subscribe( newRange => {
                    if ( !isEqual( this.range, newRange )) {
                        this.range = newRange;
                        this.handleDatasetChanges();
                    }
                }));

                this.subscriptions.push( this._uiOptionsService.getUiOptions$().subscribe( newUiOptions => {
                    this.uiOptions = newUiOptions;
                }));

                this.handleDatasetChanges();
                if ( this.datasets.length > 0 ) {
                    this.determinePlotType( this.datasets[0] );
                }

                if ( this.plotType === 'LINE' ) {
                    // only for line charts
                    this._configService.initPlotConfig( this.plotId );
                    this._filtersService.initPlotFilters( this.plotId );
                    this._menuOptionsService.initMenuOptions( this.plotId );
                    this.subscriptions.push( this._filtersService.getFilters$( this.plotId ).subscribe(
                        newFilters => {
                            if ( !isEqual( this.filters, newFilters ) ) {
                                this.filters = newFilters;
                                this.handleDatasetChanges();
                            }
                        }
                    ));
                }
            } else {
                this.handleDatasetChanges();
            }
            this.initialized = true;
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach( subscription => subscription.unsubscribe() );
    }

    generateDatasetURL( dataset: IDataset, filters?: IFilters, numBins?: number ): string {
        // TODO: make x parameter name configurable
        let url = dataset.url + addRangeConstraint( this.range, dataset.offset as number, 'time' );
        if ( filters != null ) {
            url += addFilters( dataset.type, filters, dataset.indexes.y.name );
        }
        if ( numBins != null ) {
            url += addExtraAccessParameters( dataset.type, numBins );
        }
        return url;
    }

    /** handle changes when new datasets are given to this plot */
    handleDatasetChanges() {
        if ( this.datasets.length === 0 ) {
            this.disabled = true;
        }

        // reset error flags
        this.error = '';
        this.emptyDatasets = [];

        this.datasets = this.datasets.map( dataset => {
            // make sure all required fields have values
            dataset = defaultsDeep( dataset, DEFAULT_DATASET );

            // get the datasets type
            dataset.type = determineDatasetType( dataset );

            // if dataset has an offset apply it now
            dataset.offset = parseOffset( dataset.offset );
            dataset.range = {
                start: this.range.start + dataset.offset,
                end: this.range.end + dataset.offset
            };

            // if the dataset has an events url setup events
            if ( dataset.eventsUrl != null ) {
                this.hasEvents = true;
                this._eventsService.initEventsForPlot( this.plotId, dataset.eventsUrl );
                this._eventsService.downloadEventsData( this.plotId, this.range );

                this.subscriptions.push( this._eventsService.getPlotEvents$( this.plotId ).subscribe( newEvents => {
                    this.eventsLoaded = !newEvents.loading;
                }));
            }

            if ( dataset.type === DatasetType.analog || dataset.type === DatasetType.discrete ) {
                dataset.fullURL = this.generateDatasetURL( dataset, this.filters );
            } else {
                dataset.fullURL = this.generateDatasetURL( dataset );
            }
            dataset.uid = createUID();
            return dataset;
        });

        // emit the updated datasets to the dataset service
        this._datasetService.setPlotDatasets( this.plotId, this.datasets );
    }

    determinePlotType( dataset: IDataset ) {
        if ( dataset.type === DatasetType.image ) {
            this.plotType = 'IMAGE';
        } else if ( dataset.type === DatasetType.eventTable ) {
            this.plotType = 'EVENT_TABLE';
        } else {
            this.plotType = 'LINE';
        }
    }

    handleRangeChanges() {
        this.datasets = this.datasets.map( dataset => {
            dataset.range = {
                start: this.range.start + dataset.offset,
                end: this.range.end + dataset.offset
            };
            dataset.fullURL = this.generateDatasetURL( dataset );
            return dataset;
        });

        // emit the updated datasets to the dataset service
        this._datasetService.setPlotDatasets( this.plotId, this.datasets );
    }

    removeEmptyDatasets() {
        // remove the empty datasets from our datasets list
        this.datasets = this.datasets.filter( dataset => !this.emptyDatasets.find( emptyDataset => emptyDataset.uid === dataset.uid ) );
        // if no datasets returned data, then close this plot
        if ( this.datasets.length === 0 ) {
            this._plotsService.removePlot( this.plotId );
        } else {
            this._datasetService.setPlotDatasets( this.plotId, this.datasets );
            this.emptyDatasets = [];
        }
    }
}
